import { UseQueryOptions, useQuery, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ChatThread, ChatClient } from 'api/clients/ChatClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { chatKeys } from './chatKeys'
import { ThreadsData } from './useInfiniteThreads'

interface Props {
  id?: number
}

interface Data {
  thread: ChatThread
}

const fetchThread = async (workspaceId: number, id: number) => {
  const res = await ChatClient.getThread(workspaceId, id)

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error()
  }

  return res.data
}

export const useThread = ({ id }: Props, options?: Partial<UseQueryOptions<Data>>) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useQuery<Data>(
    chatKeys.thread(workspaceId, id || 0),
    () => {
      if (!id) {
        throw new Error('Thread ID is required')
      }

      return fetchThread(workspaceId, id)
    },
    {
      placeholderData: () => {
        const cacheData = queryClient.getQueryData<ThreadsData>(chatKeys.threads(workspaceId, { items: 10 }))
        const existingThread = cacheData?.pages
          .filter((page) => page.type === 'success')
          .flatMap((page) => page.data.threads)
          .find((thread) => thread.id === id)
        if (!existingThread) return

        return {
          thread: existingThread,
        }
      },
      refetchOnWindowFocus: false,
      staleTime: (options?.cacheTime || 0) * 0.5,
      enabled: !!id,
      onSuccess: (data) => {
        queryClient.setQueryData<ThreadsData | undefined>(chatKeys.threads(workspaceId, { items: 10 }), (oldData) => {
          if (!oldData) return oldData

          return {
            ...oldData,
            pages: oldData.pages.map((page) => {
              if (page.type !== SUCCESS_RESPONSE_TYPE) return page

              return {
                ...page,
                data: {
                  ...page.data,
                  threads: page.data.threads.map((thread) =>
                    thread.id === id ? { ...thread, ...data.thread } : thread,
                  ),
                },
              }
            }),
          }
        })
      },
      ...options,
    },
  )
}
